import { useEffect, useState } from 'react';
import ruQuizzes from './quizzes/ru/index';

export type Lang = 'ru' | 'en';

/** Стабильные ключи — одинаковые в обеих локалях, подписи берутся из словарей ниже. */
export type Difficulty = 'easy' | 'medium' | 'hard' | 'hardcore';

export interface QuizQuestion {
  q: string;
  options: string[];
  correct: number;
  fact?: string;
}

export interface Quiz {
  id: string;
  title: string;
  desc: string;
  emoji: string;
  category: string;
  difficulty: Difficulty;
  questions: QuizQuestion[];
}

/** Русский список — источник правды по составу и порядку. Грузится сразу. */
export const QUIZZES: Quiz[] = ruQuizzes as Quiz[];
export const QUIZZES_RU = QUIZZES;

/** Английская локаль весит столько же, сколько русская, поэтому грузим её отдельным чанком. */
let enCache: Quiz[] | null = null;
let enPending: Promise<Quiz[]> | null = null;

export function loadQuizzes(lang: Lang): Quiz[] | Promise<Quiz[]> {
  if (lang === 'ru') return QUIZZES_RU;
  if (enCache) return enCache;
  if (!enPending) {
    enPending = import('./quizzes/en/index').then((m) => {
      enCache = m.default as Quiz[];
      return enCache;
    });
  }
  return enPending;
}

/**
 * Список квизов для локали. Русский отдаётся синхронно, английский —
 * после загрузки чанка (до этого возвращается русский, чтобы не мигала пустая страница).
 */
export function useQuizzes(lang: Lang): { quizzes: Quiz[]; ready: boolean } {
  const initial = loadQuizzes(lang);
  const [quizzes, setQuizzes] = useState<Quiz[]>(Array.isArray(initial) ? initial : QUIZZES_RU);
  const [ready, setReady] = useState(Array.isArray(initial));

  useEffect(() => {
    const result = loadQuizzes(lang);
    if (Array.isArray(result)) {
      setQuizzes(result);
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    result.then((loaded) => {
      if (cancelled) return;
      setQuizzes(loaded);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  return { quizzes, ready };
}

export const CATEGORY_LABELS: Record<Lang, Record<string, string>> = {
  ru: {
    movies: 'кино', tv: 'сериалы', anime: 'аниме', games: 'игры', music: 'музыка',
    memes: 'мемы', trivia: 'эрудиция', geo: 'география', history: 'история', science: 'наука',
    nature: 'природа', nostalgia: 'ностальгия', sports: 'спорт', food: 'еда', tech: 'техно',
    books: 'литература', art: 'искусство',
  },
  en: {
    movies: 'movies', tv: 'tv shows', anime: 'anime', games: 'games', music: 'music',
    memes: 'memes', trivia: 'trivia', geo: 'geography', history: 'history', science: 'science',
    nature: 'nature', nostalgia: 'nostalgia', sports: 'sports', food: 'food', tech: 'tech',
    books: 'books', art: 'art',
  },
};

export const DIFFICULTY_LABELS: Record<Lang, Record<Difficulty, string>> = {
  ru: { easy: 'легко', medium: 'средне', hard: 'сложно', hardcore: 'хардкор' },
  en: { easy: 'easy', medium: 'medium', hard: 'hard', hardcore: 'hardcore' },
};

export function categoryLabel(category: string, lang: Lang): string {
  return CATEGORY_LABELS[lang][category] ?? category;
}

export function difficultyLabel(difficulty: Difficulty, lang: Lang): string {
  return DIFFICULTY_LABELS[lang][difficulty] ?? difficulty;
}
